import { Icon, IconButton, Stack, Typography } from '@mui/material';
import {
  ApplicationEditablePreviewChild,
  ApplicationEditablePreviewGeneralCustomFields,
  ApplicationUpdateForm,
  CreateChildForm,
  CreateCustomField,
  CreateParentForm,
  getCustomFieldsToShow,
} from '@schooly/components/applications';
import {
  CrossIcon,
  ModalContent,
  ModalHeader,
  ModalMain,
  ReportsIcon,
  Spin,
  StudentIcon,
} from '@schooly/style';
import { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import { ApplicationEditablePreviewParent } from './ApplicationEditablePreviewParent';

interface ApplicationEditablePreviewContentProps {
  canEdit: boolean;
  headerTitle: React.ReactNode;
  footerActions: React.ReactNode;
  formData: ApplicationUpdateForm;
  withNotes?: boolean;
  parentUpdating?: Record<string, boolean>;
  childUpdating?: Record<string, boolean>;
  onClose?: () => void;
  onChildSubmit: (data: CreateChildForm, type: string) => void;
  onParentSubmit: (data: CreateParentForm, type: string) => void;
  onGeneralCustomFieldsSubmit: (data: CreateCustomField[], type: string) => void;
  onAddExistingAdult?: (adult: CreateParentForm) => void;
}

export const ApplicationEditablePreviewContent: FC<ApplicationEditablePreviewContentProps> = ({
  canEdit,
  headerTitle,
  footerActions,
  formData,
  withNotes,
  childUpdating,
  parentUpdating,
  onClose,
  onChildSubmit,
  onParentSubmit,
  onGeneralCustomFieldsSubmit,
  onAddExistingAdult,
}) => {
  const updatingIcon = (
    <Stack
      sx={(theme) => ({
        paddingRight: theme.spacing(0.5),
        color: theme.palette.primary.main,
      })}
    >
      <Icon fontSize="small">
        <Spin />
      </Icon>
    </Stack>
  );

  const generalCustomFieldsToShow = useMemo(
    () => getCustomFieldsToShow(formData.generalCustomFields ?? []),
    [formData.generalCustomFields],
  );

  return (
    <>
      <ModalHeader title={headerTitle}>
        <IconButton onClick={onClose} disabled={!onClose}>
          <CrossIcon />
        </IconButton>
      </ModalHeader>

      <ModalMain>
        <ModalContent withBorderBottom flat>
          {!!formData.parents.length && (
            <Stack
              px={2.5}
              pb={4}
              sx={(theme) => ({
                borderBottom: theme.mixins.borderValue(),
              })}
              gap={4.5}
            >
              {formData.parents.map((parent) => {
                return (
                  <ApplicationEditablePreviewParent
                    key={parent.id}
                    parent={parent}
                    onSubmit={onParentSubmit}
                    canEdit={canEdit}
                    updating={parentUpdating}
                    updatingIcon={updatingIcon}
                    withoutActionBackground
                    onAddExistingAdult={onAddExistingAdult}
                    formData={formData}
                  />
                );
              })}
            </Stack>
          )}

          {!!formData.children.length && (
            <Stack px={2.5} pt={2.5}>
              <Stack direction="row" alignItems="center" gap={1.5} pl={1.5} pb={3}>
                <Icon color="primary">
                  <StudentIcon />
                </Icon>

                <Typography variant="h1">
                  <FormattedMessage id="userType-child-plural" />
                </Typography>
              </Stack>

              {formData.children.map((child) => {
                return (
                  <ApplicationEditablePreviewChild
                    key={child.id}
                    child={child}
                    onSubmit={onChildSubmit}
                    canEdit={canEdit}
                    withNotes={withNotes}
                    updating={childUpdating}
                    updatingIcon={updatingIcon}
                  />
                );
              })}
            </Stack>
          )}

          {!!generalCustomFieldsToShow.length && (
            <Stack px={2.5} pt={2.5}>
              <Stack direction="row" alignItems="center" gap={1.5} pl={1.5} pb={3}>
                <Icon color="primary">
                  <ReportsIcon />
                </Icon>

                <Typography variant="h1">
                  <FormattedMessage id="userType-general-information" />
                </Typography>
              </Stack>

              <ApplicationEditablePreviewGeneralCustomFields
                generalCustomFields={generalCustomFieldsToShow}
                onSubmit={onGeneralCustomFieldsSubmit}
                canEdit={canEdit}
                withNotes={withNotes}
                updating={childUpdating}
                updatingIcon={updatingIcon}
              />
            </Stack>
          )}
        </ModalContent>
      </ModalMain>
      {footerActions}
    </>
  );
};
